import React, { useState, useEffect } from 'react';
import { Heart, User, MessageCircle, X, Sparkles, MoreVertical, Ban, EyeOff, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getDisplayName } from '../utils/displayName';
import { resolveImageUrl } from '../utils/imageUrl';

const LikesReceived = ({ user }) => {
  const navigate = useNavigate();
  const [likesReceived, setLikesReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuProfileId, setOpenMenuProfileId] = useState(null);
  const [blockingProfile, setBlockingProfile] = useState(null);
  const [removingProfile, setRemovingProfile] = useState(null);

  useEffect(() => {
    fetchLikesReceived();
  }, []);

  const fetchLikesReceived = async () => {
    try {
      const response = await axios.get('/matches/likes-received');
      setLikesReceived(response.data);
    } catch (error) {
      console.error('Error fetching likes received:', error);
      toast.error('Failed to load likes');
    } finally {
      setLoading(false);
    }
  };

  const handleLikeBack = async (userId) => {
    try {
      // Send a like back to create a match
      const response = await axios.post('/matches/swipe', {
        target_user_id: userId,
        action: 'like'
      });

      // Check if this creates a match
      const matchResponse = await axios.get(`/matches/check-match/${userId}`);
      if (matchResponse.data.is_match) {
        toast.success('🎉 It\'s a Match! You can now message each other!', { duration: 4000 });
        
        // Remove from likes received since it's now a match
        setLikesReceived(prev => prev.filter(like => like.user.id !== userId));
      } else {
        toast.success('Like sent! 💚', { duration: 2000 });
      }

    } catch (error) {
      console.error('Error liking back:', error);
      toast.error('Failed to like back');
    }
  };

  const handlePass = async (userId) => {
    try {
      await axios.post('/matches/swipe', {
        target_user_id: userId,
        action: 'pass'
      });

      // Remove from likes received
      setLikesReceived(prev => prev.filter(like => like.user.id !== userId));
      toast.success('Passed', { duration: 1500 });

    } catch (error) {
      console.error('Error passing:', error);
      toast.error('Failed to pass');
    }
  };

  const handleStartConversation = async (userId) => {
    try {
      // Check if they're matched
      const matchResponse = await axios.get(`/matches/check-match/${userId}`);
      if (matchResponse.data.is_match) {
        navigate('/messages', { state: { userId } });
      } else {
        toast.error('You need to match first to start a conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const handleRemoveLike = async () => {
    try {
      await axios.post('/matches/swipe', {
        target_user_id: removingProfile.user.id,
        action: 'pass'
      });
      setLikesReceived(prev => prev.filter(like => like.user.id !== removingProfile.user.id));
      toast.success('Profile removed from likes');
      setRemovingProfile(null);
    } catch (error) {
      console.error('Error removing like:', error);
      toast.error('Failed to remove profile');
    }
  };

  const handleBlockProfile = async () => {
    try {
      await axios.post(`/users/${blockingProfile.user.id}/block`);
      setLikesReceived(prev => prev.filter(like => like.user.id !== blockingProfile.user.id));
      toast.success('User blocked successfully');
      setBlockingProfile(null);
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-center">
          <div className="spinner mx-auto mb-2"></div>
          <p className="text-emerald-700 dark:text-emerald-300 text-sm">Loading likes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {likesReceived.length === 0 ? (
        <div className="text-center py-8">
          <Heart className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-2">
            No likes yet
          </h3>
          <p className="text-emerald-600 dark:text-emerald-300">
            Keep swiping to find your perfect match!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Heart className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100">
              {likesReceived.length} {likesReceived.length === 1 ? 'person likes' : 'people like'} you
            </h3>
            <Sparkles className="w-4 h-4 text-emerald-500" />
          </div>

          <div className="grid gap-4">
            {likesReceived.map((like) => (
              <div key={like.user.id} className="card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Profile Image/Avatar */}
                    <div className="relative">
                      {like.user.profile_image ? (
                        <img
                          src={resolveImageUrl(like.user.profile_image)}
                          alt={like.user.full_name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                          <User className="w-8 h-8 text-white" />
                        </div>
                      )}
                      
                      {/* Like indicator */}
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                        <Heart className="w-3 h-3 text-white fill-current" />
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100">
                        {getDisplayName(like.user.full_name)}
                      </h4>
                      <p className="text-emerald-600 dark:text-emerald-300 text-sm">
                        {like.user.age} years old • {like.user.location}
                      </p>
                      {like.compatibility_score && (
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex items-center space-x-1">
                            <Sparkles className="w-3 h-3 text-emerald-500" />
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                              {Math.round(like.compatibility_score)}% match
                            </span>
                          </div>
                        </div>
                      )}
                      {like.user.bio && (
                        <p className="text-sm text-emerald-700 dark:text-emerald-200 mt-2 line-clamp-2">
                          {like.user.bio}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Three-Dot Menu */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuProfileId(openMenuProfileId === like.user.id ? null : like.user.id);
                      }}
                      className="p-2 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </button>
                    
                    {openMenuProfileId === like.user.id && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setOpenMenuProfileId(null)}
                        />
                        
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-slate-700 rounded-lg shadow-xl z-20">
                          {/* View Profile */}
                          <button
                            onClick={() => {
                              navigate(`/profile/${like.user.id}`);
                              setOpenMenuProfileId(null);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 flex items-center space-x-3 transition-colors"
                          >
                            <User className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-emerald-800 dark:text-emerald-100 font-medium">View Profile</span>
                          </button>

                          {/* Remove from Likes */}
                          <button
                            onClick={() => {
                              setRemovingProfile(like);
                              setOpenMenuProfileId(null);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-3 transition-colors border-t border-emerald-100 dark:border-slate-700"
                          >
                            <X className="w-4 h-4 text-red-600" />
                            <span className="text-red-600 font-medium">Remove</span>
                          </button>

                          {/* Block User */}
                          <button
                            onClick={() => {
                              setBlockingProfile(like);
                              setOpenMenuProfileId(null);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-3 transition-colors border-t border-red-100 dark:border-red-900/30"
                          >
                            <Ban className="w-4 h-4 text-red-600" />
                            <span className="text-red-600 font-medium">Block</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Mental Health Goals & Interests */}
                {(like.user.mental_health_goals?.length > 0 || like.user.interests_hobbies?.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-emerald-100 dark:border-slate-700">
                    <div className="flex flex-wrap gap-2">
                      {like.user.mental_health_goals?.slice(0, 3).map((goal, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs rounded-full"
                        >
                          🎯 {goal}
                        </span>
                      ))}
                      {like.user.interests_hobbies?.slice(0, 2).map((interest, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs rounded-full"
                        >
                          ✨ {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => handlePass(like.user.id)}
                    className="flex-1 btn-secondary flex items-center justify-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Pass</span>
                  </button>
                  <button
                    onClick={() => handleLikeBack(like.user.id)}
                    className="flex-1 btn-primary flex items-center justify-center space-x-2"
                  >
                    <Heart className="w-4 h-4" />
                    <span>Like Back</span>
                  </button>
                </div>

                {/* Quick Stats */}
                {(like.user.mental_health_goals?.length > 0 || like.user.interests_hobbies?.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-emerald-100 dark:border-slate-600">
                    <div className="flex flex-wrap gap-2">
                      {like.user.mental_health_goals?.slice(0, 2).map(goal => (
                        <span key={goal} className="px-2 py-1 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 text-xs rounded-full">
                          {goal}
                        </span>
                      ))}
                      {like.user.interests_hobbies?.slice(0, 2).map(interest => (
                        <span key={interest} className="px-2 py-1 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 text-xs rounded-full">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time stamp */}
                <div className="mt-3 text-xs text-emerald-500 dark:text-emerald-400">
                  Liked you {new Date(like.created_at).toLocaleDateString('en-AU', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remove Profile Modal */}
      {removingProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-3">Remove from Likes</h3>
            <p className="text-emerald-600 dark:text-emerald-300 mb-6">
              Are you sure you want to remove <strong>{getDisplayName(removingProfile.user.full_name)}</strong> from your likes? 
              They won't be notified, and you can always like them again later.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setRemovingProfile(null)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveLike}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block User Modal */}
      {blockingProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-3">Block User</h3>
            <p className="text-emerald-600 dark:text-emerald-300 mb-6">
              Are you sure you want to block <strong>{getDisplayName(blockingProfile.user.full_name)}</strong>? 
              They will no longer be able to see your profile or contact you. You can unblock them later from settings.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setBlockingProfile(null)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockProfile}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
              >
                Block
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LikesReceived;